import { useForm } from "@mantine/form"
import { Warning } from "@phosphor-icons/react"
import {
  Card,
  Divider,
  MenuDivider,
  Modal,
  Note,
  SpinnerOverlay,
  Tag,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useSearchParams } from "react-router-dom"
import { OperationContext } from "urql"
import { CustomToast } from "~/components/CustomToast"
import TradeKindTag from "~/components/TradeKindTag"
import { TransactionDetails } from "~/components/Transaction/TransactionDetail"
import {
  HistoryColumns,
  TransactionHistory,
} from "~/components/Transaction/TransactionHistory"
import { useCreateAdvanceTransactionMutation } from "~/generated/graphql"

const INITIAL_STATE = {
  amount: undefined,
  attachments: [],
}

export const AddTransaction = ({
  refetch,
  transactions,
  tradeId,
  receiptId,
  fetching = false,
  totalAmount = 0,
  createdAt,
  kind,
  objectId,
  sellerName,
  advanceAmount,
  parameterName,
  headerTotalAmount = 0,
  historyCols = "",
  historyColSpan = "",
  bankSelectorOptions,
  remark,
  isTrade = true,
  isVerified = null,
  advanceStatus,
  columns = [
    HistoryColumns.DATE?.accessor,
    HistoryColumns.ACCOUNT_NO?.accessor,
    HistoryColumns.PAYMENT_MODE?.accessor,
    HistoryColumns.TRANSACTION_ID?.accessor,
    HistoryColumns.RELEASED_AMOUNT?.accessor,
  ],
  labels = {
    [HistoryColumns.DATE.accessor]: "Release Date",
    [HistoryColumns.ACCOUNT_NO.accessor]: "Account Number",
    [HistoryColumns.PAYMENT_MODE.accessor]: "Payment Mode",
    [HistoryColumns.TRANSACTION_ID.accessor]: "Transaction ID",
    [HistoryColumns.RELEASED_AMOUNT.accessor]: "Released Amount",
  },
}: {
  refetch?: (opts?: Partial<OperationContext> | undefined) => void
  transactions?: any[]
  fetching?: boolean
  columns?: any[]
  labels?: Record<string, string>
  tradeId?: any
  receiptId?: any
  totalAmount?: number
  sellerName?: String
  createdAt?: Date
  objectId?: number
  kind?: String
  advanceAmount?: any
  parameterName?: String
  headerTotalAmount?: any
  historyCols?: string
  historyColSpan?: string
  bankSelectorOptions?: any[]
  remark?: string | null
  isTrade?: boolean
  isVerified?: boolean | null
  advanceStatus?: string
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const advanceId = searchParams.get("advanceId")
  const isOpen = Boolean(searchParams.get("transaction")) || false
  const [, addAdvanceTransaction] = useCreateAdvanceTransactionMutation()
  const form = useForm<any>({
    initialValues: INITIAL_STATE,
    validateInputOnChange: true,
    validateInputOnBlur: true,
    clearInputErrorOnChange: false,
  })

  const [showReleaseBlockedWarning, setShowReleaseBlockedWarning] =
    useState(false)

  const releasedAmount =
    transactions?.reduce((acc, trans) => acc + Number(trans?.amount || 0), 0) ||
    0

  const remaining = Number((Number(totalAmount) - releasedAmount).toFixed(2))
  const revisedRemaining = remaining < 0 ? 0 : remaining

  const isFormDisabled = isVerified !== true
  const isReleaseComplete = advanceStatus === "Release Complete"
  const verificationNote =
    isVerified === null
      ? "This advance request is pending approval. A transaction can be added only after it is verified."
      : "This advance request has been rejected. Transaction cannot be added."

  const { values: state, setValues: setState } = form

  function getFileUploadProps(
    key: string,
    maxFiles = 4,
    shouldEncrypt = false
  ) {
    const inputProps = form.getInputProps(key)
    return {
      media: inputProps.value,
      onChange: inputProps.onChange,
      maxFiles,
      shouldEncrypt,
      onError: toast.error,
    }
  }

  useEffect(() => {
    setState((val: any) => ({
      ...val,
      amount: revisedRemaining,
    }))
  }, [revisedRemaining, transactions])

  useEffect(() => {
    const releasingAmount = Number(state?.amount || 0)
    setShowReleaseBlockedWarning(
      releasingAmount > 0 &&
        revisedRemaining > 0 &&
        releasingAmount < revisedRemaining
    )
  }, [state?.amount, revisedRemaining])

  function getInputProps(key: string) {
    const inputProps = form.getInputProps(key)
    return {
      ...inputProps,
    }
  }

  const onClose = () => {
    searchParams.delete("transaction")
    searchParams.delete("advanceId")
    setSearchParams(searchParams)
    form.reset()
  }

  const onSubmit = async () => {
    const transaction = state
    if (transaction?.amount > revisedRemaining) {
      return CustomToast({
        title: "Please enter value less than " + revisedRemaining,
        intent: "danger",
      })
    }
    if (!transaction?.mode?.id) {
      return CustomToast({
        intent: "danger",
        title: "Please select a Payment Mode",
      })
    }
    if (!transaction?.bank_name) {
      return CustomToast({
        intent: "danger",
        title: "Please select a Bank",
      })
    }

    const { error } = await addAdvanceTransaction({
      input: {
        advance_payment_id: advanceId as any,
        trade_id: tradeId,
        receipt_id: receiptId,
        transaction_id: transaction?.transaction_id,
        date: dayjs().format("YYYY-MM-DD"),
        amount: Number(transaction?.amount),
        account_no: transaction?.bank_name?.account_number,
        mode_id: transaction?.mode?.id as any,
        remark: transaction?.narration,
        attachments: transaction?.attachments?.map((att: any) => att?.id),
      },
    })

    if (error) {
      return CustomToast({
        title: "Something went wrong! " + error.message,
        intent: "danger",
      })
    }
    refetch?.({
      requestPolicy: "network-only",
    })
    onClose()

    return CustomToast({ title: "Successfully saved transaction!" })
  }

  return (
    <Modal
      bodyClassName="max-w-7xl overflow-visible"
      isOpen={isOpen}
      onClose={onClose}
      title={"Transaction Details"}
      footerClassName={classNames(
        revisedRemaining === 0 || isFormDisabled || isReleaseComplete
          ? "!hidden"
          : ""
      )}
      primaryActionButtonProps={{
        text: "Yes, Confirm",
        size: "sm",
        onClick: onSubmit,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        variant: "outline",
        size: "sm",
        onClick: onClose,
      }}
    >
      <Card className="!rounded-none p-2 bg-gray-100 text-gray-800">
        <SpinnerOverlay show={fetching} />
        <div className="flex flex-wrap gap-2 text-[14px]">
          {sellerName && (
            <>
              <div>
                <span className="font-700">{sellerName}</span>
              </div>
              <Divider />
            </>
          )}
          {createdAt && (
            <>
              <div>
                Adv. Req. Date
                <span className="font-700 pl-2">
                  {dayjs(createdAt).format("DD MMM 'YY")}
                </span>
              </div>
              <Divider />
            </>
          )}
          {objectId && (
            <>
              <div>
                Patti #<span className="font-700">{objectId}</span>
              </div>
            </>
          )}
          {kind && <TradeKindTag kind={kind as any} />}
          {parameterName && (
            <>
              <Tag intent={"warning"} className="!rounded-xl" variant="outline">
                {parameterName}
              </Tag>
              <Divider />
            </>
          )}
          {headerTotalAmount ? (
            <>
              <div>
                Total {isTrade ? "Trade" : "Receipt"} Amount
                <span className="font-700 pl-2">
                  {inr(headerTotalAmount || 0)}
                </span>
              </div>
              <Divider />
            </>
          ) : undefined}
          {advanceAmount ? (
            <>
              <div>
                Adv. Amount
                <span className="text-green font-700 pl-2">
                  {inr(advanceAmount || 0)}
                </span>
              </div>
              <Divider />
            </>
          ) : (
            <></>
          )}
          <div>
            Total Released Adv. Amount
            <span className="text-green font-700 pl-2">
              {inr(releasedAmount || 0)}
            </span>
          </div>
          {remark && (
            <>
              <Divider />
              <div>
                Remark
                <span className="font-700 pl-2">{remark}</span>
              </div>
            </>
          )}
        </div>
        {transactions?.length === 0 ? (
          <></>
        ) : (
          <>
            <MenuDivider />
            <TransactionHistory
              transactions={transactions}
              columns={columns}
              labels={labels}
              cols={historyCols}
              colSpan={historyColSpan}
            />
          </>
        )}
      </Card>

      {isReleaseComplete ? (
        <></>
      ) : isFormDisabled ? (
        <Note
          className="mx-4 my-2 border-l-4 border-yellow text-[14px]"
          intent="warning"
          title={
            (
              <div className="flex gap-2 items-center">
                <Warning className="inline" size={15} weight="fill" />
                <p>{verificationNote}</p>
              </div>
            ) as any
          }
        />
      ) : (
        <>
          {showReleaseBlockedWarning && (
            <Note
              className="mx-4 my-2 border-l-4 border-blue text-[14px]"
              intent="info"
              title={
                (
                  <div className="flex gap-2 items-center">
                    <Warning className="inline" size={15} weight="fill" />
                    <p>
                      You are releasing a partial advance amount. Utilization of
                      this advance in the {isTrade ? "trade" : "receipt"} will
                      remain blocked until the full amount is released or this
                      advance request is closed.
                    </p>
                  </div>
                ) as any
              }
            />
          )}
          {revisedRemaining !== 0 && (
            <TransactionDetails
              getFileUploadProps={getFileUploadProps}
              getInputProps={getInputProps}
              accessPath={`transaction`}
              maxFiles={4}
              application={state}
              setFieldValue={form.setFieldValue}
              uploaderClassName="row-span-4 "
              bankSelectorOptions={bankSelectorOptions}
              hidePaymentType
              hideTransactionDate
              narrationClassName="col-span-2"
            ></TransactionDetails>
          )}
        </>
      )}
    </Modal>
  )
}

export default AddTransaction
